import { TrashIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { CategorySelectField } from '../../../components/CategorySelectField';
import ConfirmableButton from '../../../components/ConfirmableButton';
import { NumberField } from '../../../components/datafields/NumberField';
import { useFieldHighlight } from '../../../components/invoice-preview/SplitPanelBookingLayout';
import { CATEGORY_OPTIONS_MAP } from '../../../constants/categoryOptions';
import { LexOfficeBookingDataLineItem } from '../../../types';
import { formatCurrency } from '../utils/formatters';

interface BookingLineItemCardProps {
  item: LexOfficeBookingDataLineItem;
  index: number;
  isEditing: boolean;
  currency: string;
  onUpdate: (index: number, field: string, value: number | string) => void;
  onRemove: (index: number) => void;
  showValidation: boolean;
}

const BookingLineItemCard: React.FC<BookingLineItemCardProps> = ({
  item,
  index,
  isEditing,
  currency,
  onUpdate,
  onRemove,
  showValidation,
}) => {
  const highlight = useFieldHighlight();
  const fieldId = `lineItem.${index}`;
  const isActive = highlight?.activeFieldId === fieldId;
  const hasMissingCategory = !item.categoryId && showValidation;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={`bg-white p-3 rounded-lg border transition-colors cursor-pointer text-sm ${
        isActive
          ? 'ring-2 ring-blue-400 bg-blue-50 border-blue-200'
          : hasMissingCategory
            ? 'bg-red-50 border-l-4 border-l-red-500 border-gray-200'
            : 'border-gray-200 hover:bg-gray-50'
      }`}
      onClick={() => highlight?.onFieldFocus(fieldId)}
      onFocus={() => highlight?.onFieldFocus(fieldId)}
    >
      {/* Header: Item label + delete button */}
      <div className='flex justify-between items-center mb-2'>
        <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
          Item {index + 1}
        </span>
        {isEditing && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div onClick={e => e.stopPropagation()}>
            <ConfirmableButton
              onConfirm={() => onRemove(index)}
              buttonClassName='p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded'
            >
              <TrashIcon className='w-4 h-4' />
            </ConfirmableButton>
          </div>
        )}
      </div>

      {/* Fields */}
      <div className='space-y-2'>
        <NumberField
          label='Amount'
          value={item.amount}
          onChange={value => onUpdate(index, 'amount', value)}
          isEditing={isEditing}
          formatter={v => formatCurrency(v, currency)}
        />
        <NumberField
          label='Tax Amount'
          value={item.taxAmount}
          onChange={value => onUpdate(index, 'taxAmount', value)}
          isEditing={isEditing}
          formatter={v => formatCurrency(v, currency)}
        />
        <NumberField
          label='Tax Rate'
          value={item.taxRatePercent}
          onChange={value => onUpdate(index, 'taxRatePercent', value)}
          isEditing={isEditing}
          formatter={v => `${v}%`}
        />

        {/* Category */}
        <div className='flex justify-between items-center'>
          <span className='text-gray-600'>Category:</span>
          {isEditing ? (
            <div className='min-w-[200px]'>
              <CategorySelectField
                value={item.categoryId || ''}
                onChange={value => onUpdate(index, 'categoryId', value)}
                isEditing={true}
                hasError={hasMissingCategory}
                label=''
              />
            </div>
          ) : (
            <span
              className={`font-medium ${
                hasMissingCategory ? 'text-red-600' : 'text-gray-800'
              }`}
            >
              {CATEGORY_OPTIONS_MAP[item.categoryId] || (
                <span className='italic text-red-600'>Missing category</span>
              )}
            </span>
          )}
        </div>

        {hasMissingCategory && (
          <p className='text-xs text-red-600'>Category is required</p>
        )}
      </div>
    </div>
  );
};

export default BookingLineItemCard;
